import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_DEVICE } from '@/queries/devices';
import { getAppVersion } from './useAppVersion';

/**
 * Keeps the backend's `device.appVersion` field in sync with the currently
 * running ORB app version so Backoffice's Devices screen reflects reality
 * without requiring a re-pair.
 *
 * Fires once per layout mount (i.e. once per app launch / login, since
 * `DeviceConfigLayout` wraps all device-authenticated routes). No
 * cached-vs-current comparison — the update mutation is idempotent and
 * ORB sessions are long-lived, so always-push is simpler and safe.
 *
 * The mutation is a no-op when device id/name are missing or when the
 * version can't be resolved. Failures are logged and swallowed so a
 * sync error never blocks ORB usage.
 *
 * @param deviceId  id of the currently selected device; skip when undefined.
 * @param deviceName name of the currently selected device; required by the
 *                   `UpdateDeviceInput` GraphQL schema.
 */
export function useAppVersionSync(
  deviceId: string | undefined,
  deviceName: string | undefined,
): void {
  const [updateDevice] = useMutation(UPDATE_DEVICE);

  useEffect(() => {
    if (!deviceId || !deviceName) return;

    let cancelled = false;
    (async () => {
      try {
        const appVersion = await getAppVersion();
        if (cancelled || appVersion === 'unknown') return;
        await updateDevice({
          variables: {
            input: { id: deviceId, name: deviceName, appVersion },
          },
        });
      } catch (err) {
        if (!cancelled) console.error('appVersion sync failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deviceId, deviceName, updateDevice]);
}
